import {
    ChangeEvent,
    forwardRef,
    ForwardRefExoticComponent,
    MutableRefObject,
    ReactElement,
    Ref,
    RefAttributes,
    TextareaHTMLAttributes,
    useLayoutEffect,
    useRef,
} from 'react'
import classNames from 'classnames'

import './Textarea.scss'

interface TextareaProps extends RefAttributes<HTMLTextAreaElement> {
    error?: string
    autoGrow?: boolean
    minRows?: number
    maxRows?: number
}

export type HTMLTextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & TextareaProps;

const resizeToFitContent = (element: HTMLTextAreaElement, maxRows?: number): void => {
    const borderHeight = element.offsetHeight - element.clientHeight

    element.style.height = 'auto'

    const lineHeight = parseFloat(getComputedStyle(element).lineHeight)
    const maxHeight = maxRows && ! Number.isNaN(lineHeight) ? lineHeight * maxRows : Infinity
    const contentHeight = Math.min(element.scrollHeight, maxHeight)

    element.style.height = `${contentHeight + borderHeight}px`
    element.style.overflowY = element.scrollHeight > maxHeight ? 'auto' : 'hidden'
}

export const Textarea: ForwardRefExoticComponent<HTMLTextareaProps> = forwardRef(({
    error = '',
    autoGrow = false,
    minRows = 1,
    maxRows,
    rows,
    value,
    onChange,
    className = '',
    ...textareaProps
}, forwardedRef: Ref<HTMLTextAreaElement>): ReactElement => {
    const internalRef = useRef<HTMLTextAreaElement | null>(null)

    const setRefs = (element: HTMLTextAreaElement | null): void => {
        internalRef.current = element

        if (typeof forwardedRef === 'function') {
            forwardedRef(element)
        } else if (forwardedRef) {
            (forwardedRef as MutableRefObject<HTMLTextAreaElement | null>).current = element
        }
    }

    // Also covers value changes from outside, like resetting the form
    useLayoutEffect(() => {
        if (autoGrow && internalRef.current) {
            resizeToFitContent(internalRef.current, maxRows)
        }
    }, [autoGrow, maxRows, value])

    const handleChange = (event: ChangeEvent<HTMLTextAreaElement>): void => {
        onChange?.(event)

        if (autoGrow) {
            resizeToFitContent(event.currentTarget, maxRows)
        }
    }

    const textareaClassNames = classNames('textarea-input', {
        'textarea-input--has-error': !! error,
        'textarea-input--auto-grow': autoGrow,
    }, className)

    return (
        <textarea
            {...textareaProps}
            ref={setRefs}
            rows={rows ?? (autoGrow ? minRows : undefined)}
            value={value}
            onChange={handleChange}
            className={textareaClassNames}
        />
    )
})
